import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const BYLAWS_URL = "https://customer-assets.emergentagent.com/job_divine-manager-1/artifacts/dy8wcr4c_Nadja%20Scott%20And%20DA%20Bylaws%202024-2026%20updated%20%28edited%29.pdf";

export const ChoirTest = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', part: '', motivation: '', personality: '',
    is_soloist: '',
    rehearsal: '', travel: '', planning: '',
    reliable_transportation: '',
    communicative: '',
    finance_travel: '', finance_understand: '',
    monthly_donation: '',
    practice: '', direction: '', confidence: '',
    affirm: false,
    score_vision: '', score_unity: '', score_conflict: '', score_why: ''
  });

  const [showResult, setShowResult] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showWelcome, setShowWelcome] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  // Show DMV chapter popup only when user navigates to this section
  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('da_popup_seen');
    
    if (!hasSeenPopup && window.location.hash === '#da-membership') {
      const timer = setTimeout(() => {
        setShowPopup(true);
        sessionStorage.setItem('da_popup_seen', 'true');
      }, 500);
      return () => clearTimeout(timer);
    }

    // Also check if hash changes to this section
    const handleHashChange = () => {
      if (window.location.hash === '#da-membership' && !sessionStorage.getItem('da_popup_seen')) {
        setShowPopup(true);
        sessionStorage.setItem('da_popup_seen', 'true');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const playWelcomeMessage = () => {
    const message = "Welcome to the assessment. Thank you for considering Divine Anointing Choir. Our prayer is that we are hopeful that our family is a place where you can grow, thrive and find your place in our family.";
    const utterance = new SpeechSynthesisUtterance(message);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => setIsPlaying(false);
    
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  const startAssessment = () => {
    setShowWelcome(false);
    playWelcomeMessage();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Skip step logic since all fields are shown on single page
    // if (currentStep < 4) {
    //   nextStep();
    //   return;
    // }

    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare submission data
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        part: formData.part || 'Not specified',
        motivation: formData.motivation || '',
        personality: formData.personality || '',
        is_soloist: formData.is_soloist || '',
        rehearsal: Number(formData.rehearsal),
        travel: Number(formData.travel),
        planning: Number(formData.planning),
        reliable_transportation: formData.reliable_transportation || '',
        communicative: formData.communicative || '',
        finance_travel: Number(formData.finance_travel),
        finance_understand: Number(formData.finance_understand),
        monthly_donation: formData.monthly_donation || '',
        practice: Number(formData.practice),
        direction: Number(formData.direction),
        confidence: Number(formData.confidence),
        vision: '', // Now multiple choice - score only
        unity: '', // Now multiple choice - score only
        conflict: '', // Now multiple choice - score only
        why: '', // Now multiple choice - score only
        score_vision: Number(formData.score_vision),
        score_unity: Number(formData.score_unity),
        score_conflict: Number(formData.score_conflict),
        score_why: Number(formData.score_why)
      };

      const response = await fetch(`${BACKEND_URL}/api/applications/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        throw new Error('Submission failed. Please try again.');
      }

      const result = await response.json();
      setSubmissionResult(result);
      setShowResult(true);
      
      setTimeout(() => {
        const resultEl = document.getElementById('result');
        if (resultEl) resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '', email: '', part: '', motivation: '', personality: '',
      is_soloist: '',
      rehearsal: '', travel: '', planning: '',
      reliable_transportation: '',
      communicative: '',
      finance_travel: '', finance_understand: '',
      monthly_donation: '',
      practice: '', direction: '', confidence: '',
      affirm: false, score_vision: '', score_unity: '', score_conflict: '', score_why: ''
    });
    setShowResult(false);
    setSubmissionResult(null);
    setError(null);
  };

  const stepTitles = [
    "About You",
    "Commitment & Availability", 
    "Financial & Singing",
    "Alignment with DA Vision",
    "Review & Submit"
  ];

  const styles = {
    wrap: { maxWidth: '980px', margin: '2rem auto', background: 'white', borderRadius: '16px', border: '1px solid #e6e2d9', overflow: 'hidden' },
    hero: { background: 'linear-gradient(120deg, #0b0b0c 0%, #2b2b2e 60%, #0b0b0c 100%)', color: 'white', padding: '1.75rem 1.5rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' },
    heroImg: { width: '72px', height: '72px', objectFit: 'cover', borderRadius: '12px', border: '2px solid #c8a43a' },
    inner: { padding: '1.25rem 1.5rem 1.75rem', background: '#f7f6f2', minHeight: '500px' },
    fieldset: { border: '1px solid #e6e2d9', padding: '1rem', margin: '1rem 0', borderRadius: '12px', background: 'white', animation: 'fadeIn 0.5s ease-in' },
    legend: { fontWeight: 700, color: '#c8a43a', padding: '0 0.25rem' },
    input: { width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #e6e2d9', borderRadius: '10px', transition: 'all 0.3s ease' },
    textarea: { width: '100%', minHeight: '90px', padding: '0.6rem 0.7rem', border: '1px solid #e6e2d9', borderRadius: '10px', transition: 'all 0.3s ease' },
    btn: { background: '#c8a43a', color: '#111827', padding: '0.8rem 1.5rem', border: 0, borderRadius: '10px', fontWeight: 700, cursor: 'pointer', marginRight: '0.5rem', transition: 'all 0.3s ease', boxShadow: '0 2px 8px rgba(200,164,58,0.3)' },
    btnSecondary: { background: '#e5e7eb', color: '#111827', padding: '0.8rem 1.5rem', border: 0, borderRadius: '10px', fontWeight: 700, cursor: 'pointer', marginRight: '0.5rem', transition: 'all 0.3s ease' },
    btnDisabled: { background: '#9ca3af', color: '#fff', padding: '0.8rem 1.5rem', border: 0, borderRadius: '10px', fontWeight: 700, cursor: 'not-allowed', marginRight: '0.5rem' },
    result: { marginTop: '1rem', padding: '1.5rem', borderRadius: '12px', border: '2px solid #c8a43a', background: '#fff', animation: 'fadeIn 0.5s ease-in' },
    small: { fontSize: '0.85rem', color: '#475569' },
    progressBar: { 
      width: '100%', 
      height: '8px', 
      background: '#e5e7eb', 
      borderRadius: '4px', 
      overflow: 'hidden',
      marginBottom: '1.5rem'
    },
    progressFill: { 
      height: '100%', 
      background: 'linear-gradient(90deg, #c8a43a 0%, #d4af37 100%)', 
      transition: 'width 0.5s ease',
      boxShadow: '0 0 10px rgba(200,164,58,0.5)'
    },
    stepIndicator: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '2rem',
      padding: '0 1rem'
    },
    stepDot: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      transition: 'all 0.3s ease'
    },
    welcomeScreen: {
      textAlign: 'center',
      padding: '3rem 2rem',
      animation: 'fadeIn 0.5s ease-in'
    },
    popup: { 
      position: 'fixed', 
      top: '50%', 
      left: '50%', 
      transform: 'translate(-50%, -50%)', 
      background: 'linear-gradient(135deg, #dc2626 0%, #0b0b0c 100%)',
      color: 'white', 
      padding: '2.5rem', 
      borderRadius: '20px', 
      border: '4px solid #c8a43a', 
      zIndex: 9999, 
      maxWidth: '550px',
      boxShadow: '0 25px 80px rgba(220, 38, 38, 0.5), 0 0 0 1px rgba(200, 164, 58, 0.3)'
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      zIndex: 9998,
      backdropFilter: 'blur(4px)'
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        input:focus, textarea:focus, select:focus {
          outline: none;
          border-color: #c8a43a !important;
          box-shadow: 0 0 0 3px rgba(200, 164, 58, 0.1) !important;
        }
        button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(200, 164, 58, 0.4) !important;
        }
        button:active:not(:disabled) {
          transform: translateY(0);
        }
      `}</style>
      <section id="da-membership" className="py-20" style={{ background: '#000' }}>
        {/* DMV Chapter Popup */}
      {showPopup && (
        <>
          <div style={styles.overlay} onClick={() => setShowPopup(false)} />
          <div style={styles.popup}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ 
                fontSize: '3.5rem', 
                marginBottom: '0.5rem',
                filter: 'drop-shadow(0 0 10px rgba(200, 164, 58, 0.5))'
              }}>
                ⚠️
              </div>
              <h2 style={{ 
                margin: '0 0 0.5rem 0', 
                fontSize: '2rem', 
                color: '#c8a43a',
                textShadow: '0 2px 10px rgba(200, 164, 58, 0.3)',
                fontWeight: 800
              }}>
                Important Notice
              </h2>
            </div>
            <p style={{ 
              margin: '0 0 1.5rem 0', 
              fontSize: '1.2rem', 
              lineHeight: 1.7,
              textAlign: 'center',
              background: 'rgba(255,255,255,0.1)',
              padding: '1rem',
              borderRadius: '10px',
              border: '2px solid rgba(200, 164, 58, 0.3)'
            }}>
              This membership assessment is for the<br/>
              <strong style={{ 
                fontSize: '1.4rem', 
                color: '#c8a43a',
                display: 'inline-block',
                marginTop: '0.5rem'
              }}>
                DMV/Maryland Chapter Only
              </strong>
            </p>
            <button 
              onClick={() => setShowPopup(false)} 
              style={{ 
                ...styles.btn, 
                marginTop: '0.5rem', 
                width: '100%',
                fontSize: '1.1rem',
                padding: '1rem',
                boxShadow: '0 4px 15px rgba(200, 164, 58, 0.4)'
              }}
            >
              ✓ I Understand
            </button>
          </div>
        </>
      )}

      <div style={styles.wrap}>
        <div style={styles.hero}>
          <img 
            src="https://customer-assets.emergentagent.com/job_nadja-divine-choir/artifacts/d8wqvckb_Anointing%20-%201.png" 
            alt="Divine Anointing logo" 
            style={styles.heroImg}
          />
          <div>
            <h1 style={{ fontSize: '1.9rem', margin: 0 }}>DA Membership Assessment</h1>
            <p style={{ margin: '0.35rem 0 0 0' }}>Nadja Scott and Divine Anointing Choir - DMV/Maryland Chapter<br/>Industry standard traveling and recording choir. Monday rehearsals 7-9 PM.</p>
          </div>
        </div>

        <div style={styles.inner}>
          {!showResult ? (
            <form onSubmit={handleSubmit}>
            
            {/* About You */}
            <fieldset style={styles.fieldset}>
              <legend style={styles.legend}>About You</legend>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <div style={{ flex: '1 1 260px' }}>
                  <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Full Name <span style={{ color: '#ef4444' }}>*</span></p>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required style={styles.input} />
                </div>
                <div style={{ flex: '1 1 260px' }}>
                  <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Email <span style={{ color: '#ef4444' }}>*</span></p>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required style={styles.input} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Phone Number <span style={{ color: '#ef4444' }}>*</span></p>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="(123) 456-7890" style={styles.input} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Vocal Part</p>
                  <select name="part" value={formData.part} onChange={handleChange} style={styles.input}>
                    <option value="">Select</option>
                    <option>Soprano</option>
                    <option>Alto</option>
                    <option>Tenor</option>
                    <option>Not sure</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>What motivates you to sing and serve in ministry</p>
                <textarea name="motivation" value={formData.motivation} onChange={handleChange} placeholder="Share from the heart" style={styles.textarea} />
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Describe your personality in three words</p>
                <input type="text" name="personality" value={formData.personality} onChange={handleChange} placeholder="dependable, joyful, teachable" style={styles.input} />
              </div>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Are you a soloist?</p>
                {['Yes', 'No'].map(opt => (
                  <label key={opt} style={{ display: 'inline-block', margin: '0 1rem 0 0' }}>
                    <input type="radio" name="is_soloist" value={opt} checked={formData.is_soloist === opt} onChange={handleChange} required /> {opt}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Commitment */}
            <fieldset style={styles.fieldset}>
              <legend style={styles.legend}>Commitment and Availability</legend>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Can you consistently attend Monday rehearsals 7-9 PM</p>
                {['Yes, always|4', 'Most of the time|3', 'Occasionally|2', 'No|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="rehearsal" value={val} checked={formData.rehearsal === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Willing to travel and record with the choir</p>
                {['Yes|4', 'Depends on the event|2', 'No|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="travel" value={val} checked={formData.travel === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>How do you plan for choir dates alongside work/school</p>
                {['I calendar everything and protect rehearsal times|4', 'I plan ahead most weeks|3', 'I plan close to the date|2', 'I handle conflicts last minute|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="planning" value={val} checked={formData.planning === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Do you have reliable transportation to travel to Baltimore for rehearsals weekly?</p>
                {['Yes', 'No'].map(opt => (
                  <label key={opt} style={{ display: 'block', margin: '0.25rem 0' }}>
                    <input type="radio" name="reliable_transportation" value={opt} checked={formData.reliable_transportation === opt} onChange={handleChange} required /> {opt}
                  </label>
                ))}
              </div>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Ministry dates are often given well in advance with opportunities to tell us when you are unavailable. Can you agree to be communicative when absent or have a need or question?</p>
                {['Yes', 'No'].map(opt => (
                  <label key={opt} style={{ display: 'block', margin: '0.25rem 0' }}>
                    <input type="radio" name="communicative" value={opt} checked={formData.communicative === opt} onChange={handleChange} required /> {opt}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Financial */}
            <fieldset style={styles.fieldset}>
              <legend style={styles.legend}>Financial Responsibility</legend>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Can you contribute toward travel or attire when needed</p>
                {['Yes, I can budget for it|4', 'Possibly, with notice|3', 'No, not at this time|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="finance_travel" value={val} checked={formData.finance_travel === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Do you understand choir funds support ministry operations</p>
                {['Yes, and I support that|4', 'I am unsure|2', 'No|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="finance_understand" value={val} checked={formData.finance_understand === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>We sow a monthly donation to the ministry to help with upkeep of the ministry. Will you be able to satisfy this donation monthly of $20?</p>
                {['Yes', 'No'].map(opt => (
                  <label key={opt} style={{ display: 'block', margin: '0.25rem 0' }}>
                    <input type="radio" name="monthly_donation" value={opt} checked={formData.monthly_donation === opt} onChange={handleChange} required /> {opt}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Singing */}
            <fieldset style={styles.fieldset}>
              <legend style={styles.legend}>Singing Discipline</legend>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>How often do you practice outside rehearsal</p>
                {['Daily|4', 'Weekly|3', 'Only before performances|2', 'Rarely|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="practice" value={val} checked={formData.practice === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>How do you handle musical direction that stretches you</p>
                {['I welcome it and adjust quickly|4', 'I try and ask for help|3', 'I struggle but may adapt later|2', 'I prefer to do it my way|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="direction" value={val} checked={formData.direction === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Rate your confidence learning new material quickly (1-5)</p>
                {['1', '2', '3', '4', '5'].map(val => (
                  <label key={val} style={{ display: 'inline-block', margin: '0 0.5rem' }}><input type="radio" name="confidence" value={val} checked={formData.confidence === val} onChange={handleChange} required /> {val}</label>
                ))}
              </div>
            </fieldset>

            {/* Alignment */}
            <fieldset style={styles.fieldset}>
              <legend style={styles.legend}>Alignment with DA Vision</legend>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>What does ministry and industry excellence mean to you</p>
                {['Bringing glory to God through professional, anointed ministry|4', 'Singing well and serving faithfully|3', 'Doing my best when I can|2', 'I am still learning what this means|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="score_vision" value={val} checked={formData.score_vision === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>How do you contribute to unity and professionalism</p>
                {['I lead by example, encourage others, and maintain high standards|4', 'I stay positive and work well with the team|3', 'I follow directions and try to get along|2', 'I am working on being more consistent|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="score_unity" value={val} checked={formData.score_unity === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>How do you handle conflict in a team</p>
                {['I address it privately with grace, seeking resolution and understanding|4', 'I try to talk it out and find common ground|3', 'I usually avoid it or let leadership handle it|2', 'Conflict is difficult for me to navigate|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="score_conflict" value={val} checked={formData.score_conflict === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Why Divine Anointing specifically</p>
                {['I am called to this ministry and deeply aligned with the vision and culture|4', 'I admire the professionalism and want to grow here|3', 'I am looking for a choir to join|2', 'I heard about it and wanted to try|1'].map(opt => {
                  const [label, val] = opt.split('|');
                  return <label key={val} style={{ display: 'block', margin: '0.25rem 0' }}><input type="radio" name="score_why" value={val} checked={formData.score_why === val} onChange={handleChange} required /> {label}</label>;
                })}
              </div>
            </fieldset>

            <div style={{ margin: '0.75rem 0' }}>
              <label style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="checkbox" name="affirm" checked={formData.affirm} onChange={handleChange} required />
                <span>I affirm my answers are honest and I can meet expectations</span>
              </label>
            </div>

            {error && (
              <div style={{ padding: '1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '10px', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button 
                type="submit" 
                style={isSubmitting ? styles.btnDisabled : styles.btn}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
              </button>
              <button type="button" onClick={handleReset} style={styles.btn}>Reset</button>
            </div>
            </form>
          ) : null}

          {/* Success Screen - Replaces Entire Form */}
          {showResult && submissionResult && (
              <div id="result" style={styles.result}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', color: '#c8a43a' }}>
                  Assessment Submitted Successfully!
                </h2>

                {(submissionResult.tier === 'Gold' || submissionResult.tier === 'Silver') ? (
                  <>
                    <p style={{ fontSize: '1.1rem', lineHeight: 1.8, margin: '0 0 1.5rem 0' }}>
                      <strong>Congratulations!</strong> An ambassador will contact you in 2-3 business days for your official welcome to the ministry and next steps.
                    </p>
                    <p style={{ fontSize: '1rem', lineHeight: 1.6, margin: '0 0 1.5rem 0' }}>
                      While you wait, please download and read the bylaws below. Be prepared with any questions you may have.
                    </p>
                    <a 
                      href={BYLAWS_URL}
                      download="DA_Bylaws_2024-2026.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        display: 'inline-block',
                        background: '#c8a43a', 
                        color: '#111827', 
                        padding: '1rem 1.5rem', 
                        borderRadius: '10px', 
                        fontWeight: 700, 
                        textDecoration: 'none',
                        fontSize: '1rem'
                      }}
                    >
                      📄 Download Bylaws
                    </a>
                    <p style={{ ...styles.small, marginTop: '1rem' }}>
                      Application ID: {submissionResult.application_id}
                    </p>
                  </>
                ) : (
                  <>
                    <p style={{ fontSize: '1.1rem', lineHeight: 1.8, margin: '0 0 1rem 0' }}>
                      Thank you for completing the assessment for membership.
                    </p>
                    <p style={{ fontSize: '1rem', lineHeight: 1.6, margin: 0 }}>
                      An ambassador will contact you in 2-3 days.
                    </p>
                    <p style={{ ...styles.small, marginTop: '1rem' }}>
                      Application ID: {submissionResult.application_id}
                    </p>
                  </>
                )}
              </div>
            )}

          <p style={{ ...styles.small, marginTop: '1rem' }}>
            Questions? Email <a href="mailto:info@thehealinghousecollective.org">info@thehealinghousecollective.org</a> or visit <a href="https://www.nadjascott.com">www.nadjascott.com</a>
          </p>
        </div>
      </div>
    </section>
    </>
  );
};
